import React, { useState } from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useAppContext } from '../../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { useCmsText } from '../../hooks/useCmsText';
import { CountryCode } from 'libphonenumber-js';
import { useSendEmailMutation } from '../../store/api';

interface PartnerContacts {
    phone?: string;
    email?: string;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CustomerServicePage = () => {
    const { state } = useAppContext();
    const navigate = useNavigate();
    const { renderPlainText: renderText  } = useCmsText();
    const [email, setEmail] = useState<string>('');
    const [phone, setPhone] = useState<string | undefined>('');
    const [error, setError] = useState<string>('');
    const [emailError, setEmailError] = useState<string>('');
    const [phoneError, setPhoneError] = useState<string>('');

    const [sendEmail, { isLoading }] = useSendEmailMutation();

    const validateForm = () => {
        let isValid = true;
        
        if (!emailRegex.test(email)) {
            setEmailError('Vennligst skriv inn en gyldig e-postadresse');
            isValid = false;
        } else {
            setEmailError('');
        }

        if (phone && !isValidPhoneNumber(phone)) {
            setPhoneError('Ugyldig telefonnummer');
            isValid = false;
        } else {
            setPhoneError('');
        }

        return isValid;
    };

    const defaultCountryCode = () => {
        const lang = state.selectedLanguage;

        switch (lang) {
            case 'NORSK':
                return 'NO';
            case 'DANSK':
                return 'DK';
            case 'SVENSK':
                return 'SE';
            case 'ENG':
                return 'US';
            default:
                return 'NO';
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) return;

        try {
            await sendEmail({
                To: [{
                    Email: state.partnerEmail,
                    Name: state.partnerName
                }],
                Subject: `${state.partnerName}: ${state.caseId}`,
                Content: `Kontaktinformasjon:
                    E-post: ${email}
                    Telefon: ${phone || 'Ikke oppgitt'}

                    Case Link: https://hub.konciv.com/${state.caseId}`,
                From: {
                    Email: "notifications@konciv.com",
                    Name: "Konciv Notifications"
                }
            }).unwrap();
            navigate('/thank-you');
        } catch (err) {
            console.error('Error sending email:', err);
            setError('Noe gikk galt ved sending av skjema. Vennligst prøv igjen.');
        }
    };    

    return (
        <div className='w-full md:w-2/3 lg:w-1/3 mx-auto flex flex-col px-4 md:px-8 lg:px-12 py-5'>
            <button onClick={() => navigate(-1)} className="self-start mb-4 text-lg font-medium ml-2" style={{ color: state.branding?.fontColor }}>
                ← {renderText('cs_button_back', 'Tilbake')}
            </button>

            <div className="p-4">
                <h2 className="text-xl font-semibold mb-2" style={{ color: state.branding?.fontColor }}>
                    {renderText('cs_header', 'Send til kundeservice')}
                </h2>
                
                <p className="text-sm mb-4" style={{ color: state.branding?.fontColor }}>
                    E-post: {renderText('cs_email_label', 'E-post')}
                </p>
                <p className="text-sm mb-4" style={{ color: state.branding?.fontColor }}>
                    Telefonnummer: {renderText('cs_phone_label', 'Telefonnummer')}
                </p>

                <div className="mb-4">
                    <p className="font-medium" style={{ color: state.branding?.fontColor }}>
                        {renderText('customer_service_call_us', 'Ring oss:')}{' '}
                        {/* <a href={`tel:${state.partnerPhone}`} className="text-blue-600">
                            {state.partnerPhone}
                        </a> */}
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">
                            <p style={{ color: state.branding?.fontColor }}>{renderText('cs_email_label', 'E-post (påkrevd)')}</p>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full p-2 border rounded-md mt-1"
                                required
                                onInvalid={(e) =>
                                    (e.target as HTMLInputElement).setCustomValidity(
                                        renderText('customer_service_required_field', 'Fyll ut dette feltet')
                                    )
                                }
                            />
                            {emailError && <p className="text-red-500 text-xs mt-1">{emailError}</p>}
                        </label>
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">
                            <p style={{ color: state.branding?.fontColor }}>{renderText('cs_phone_label', 'Telefonnummer (valgfritt)')}</p>
                            <PhoneInput
                                international
                                defaultCountry={defaultCountryCode() as CountryCode}
                                value={phone}
                                onChange={setPhone}
                                className="phone-input-wrapper mt-1 w-full"
                            />
                            {phoneError && <p className="text-red-500 text-xs mt-1">{phoneError}</p>}
                        </label>
                    </div>

                    {error && <p className="text-red-500 text-sm mb-2">{error}</p>}

                    <button
                        type="submit"
                        className="w-full bg-slate-800 text-white py-2 px-4 rounded-md hover:bg-slate-700 transition-colors"
                        style={{ backgroundColor: state.branding?.mainButtonBackground, font: state.branding?.mainButtonFont }}
                    >
                        {renderText('cs_button_send', 'Send til kundeservice')}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CustomerServicePage;